import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Phone, MessageSquare } from "lucide-react";
import Logo from "../components/Logo";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import LegalFooter from "../components/LegalFooter";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (user === undefined) {
    return <div className="h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/worker"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password.trim());
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left – brand panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#09090B] text-white p-12 flex-col justify-between">
        {/* Background image collage */}
        <div
          className="absolute inset-0 grid grid-cols-2 grid-rows-3 gap-1 opacity-100 pointer-events-none"
          style={{ filter: "brightness(1.3)" }}
        >
          <img
            src="https://images.unsplash.com/photo-1496588152823-86ff7695e68f?w=900&auto=format&fit=crop&q=70"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <img
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=900&auto=format&fit=crop&q=70"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <img
            src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&auto=format&fit=crop&q=70"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&auto=format&fit=crop&q=70"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <img
            src="https://images.unsplash.com/photo-1581578017093-cd30fce4eeb7?w=900&auto=format&fit=crop&q=70"
            alt=""
            className="w-full h-full object-cover col-span-2"
            loading="eager"
          />
        </div>

        {/* Dark overlay for readability (lets imagery show through) */}
        <div className="absolute inset-0 bg-[#09090B]/45 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#09090B]/20 via-[#09090B]/45 to-[#09090B]/80 pointer-events-none" />

        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Top: small logo lockup */}
        <div className="relative z-10 flex items-center gap-3">
          <Logo size="md" invert />
          <div>
            <div className="font-black tracking-tighter text-xl leading-none">AVIDITY</div>
            <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">Cleaning Services</div>
          </div>
        </div>

        {/* Centre: brand statement */}
        <div className="relative z-10">
          <h1
            className="font-black tracking-tighter text-6xl xl:text-7xl 2xl:text-8xl leading-[0.95] mb-3"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 0 40px rgba(0,0,0,0.7)" }}
            data-testid="brand-title"
          >
            <span className="block whitespace-nowrap">Avidity</span>
            <span className="block whitespace-nowrap text-5xl xl:text-6xl 2xl:text-7xl">Cleaning Services</span>
          </h1>
          <p
            className="text-white italic text-base xl:text-lg tracking-wide mb-8 whitespace-nowrap"
            style={{ textShadow: "0 1px 10px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.6)" }}
          >
            Standards You Can See. Service You Can Trust.
          </p>

          {/* Bookings & enquiries */}
          <div data-testid="bookings-block">
            <div className="text-[10px] uppercase tracking-widest text-white/70 mb-2">
              Bookings & Enquiries
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href="tel:0416032424"
                data-testid="brand-call-link"
                className="inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur border border-white/40 hover:border-white hover:bg-white/20 transition rounded-sm"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}
              >
                <Phone className="w-5 h-5" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest text-white/70">Call</div>
                  <div className="font-black text-xl tracking-tight">0416 032 424</div>
                </div>
              </a>
              <a
                href="sms:0416032424"
                data-testid="brand-message-link"
                className="inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur border border-white/40 hover:border-white hover:bg-white/20 transition rounded-sm"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}
              >
                <MessageSquare className="w-5 h-5" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest text-white/70">Message</div>
                  <div className="font-black text-xl tracking-tight">0416 032 424</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom: links */}
        <div className="relative z-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs tracking-widest uppercase">
          <span className="text-white/50">v1 · scheduler</span>
          <a
            href="https://aviditycleaningservices.com.au"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="brand-website-link"
            className="text-white/90 hover:text-white border-b border-white/40 hover:border-white pb-0.5 transition"
          >
            aviditycleaningservices.com.au
          </a>
        </div>
      </div>

      {/* Right – form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="w-full max-w-md"
        >
          <div className="mb-10 flex items-center gap-4 lg:hidden">
            <Logo size="md" />
            <div>
              <div className="font-black tracking-tighter text-lg leading-none text-gray-900">AVIDITY</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Cleaning Services</div>
            </div>
          </div>
          <div className="mb-10">
            <h2 className="font-black tracking-tighter text-4xl text-gray-900">Sign in</h2>
            <p className="text-gray-500 mt-2">Welcome back. Manage shifts or check yours.</p>
          </div>

          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-gray-300 px-4 py-3 mb-5 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none rounded-sm font-medium"
          />

          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full border border-gray-300 px-4 py-3 mb-6 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none rounded-sm font-medium"
          />

          {err && (
            <div data-testid="login-error" className="mb-4 px-3 py-2 border border-red-300 bg-red-50 text-red-800 text-sm rounded-sm">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-[#002FA7] text-white py-3 font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm transition"
          >
            {loading ? "SIGNING IN…" : "SIGN IN"}
          </button>

          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            data-testid="login-forgot-link"
            className="mt-3 w-full text-xs text-[#002FA7] hover:underline font-semibold"
          >
            Forgot your password?
          </button>

          <ForgotPasswordModal
            open={forgotOpen}
            onClose={() => setForgotOpen(false)}
            initialEmail={email}
          />

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
            <a
              href="tel:0416032424"
              data-testid="form-call-link"
              className="text-[#002FA7] hover:underline font-bold flex items-center gap-1"
            >
              <Phone className="w-3.5 h-3.5" /> Call 0416 032 424
            </a>
            <a
              href="sms:0416032424"
              data-testid="form-message-link"
              className="text-[#002FA7] hover:underline font-bold flex items-center gap-1"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Message
            </a>
            <a
              href="https://aviditycleaningservices.com.au"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="form-website-link"
              className="text-[#002FA7] hover:underline font-semibold"
            >
              Website ↗
            </a>
          </div>
        </form>
      </div>
      <LegalFooter className="mt-auto" />
    </div>
  );
}
